import type { IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { faCircleInfo as duoLightCircleInfo} from '@fortawesome/duotone-light-svg-icons';
import { faCircleInfo as duoRegCircleInfo} from '@fortawesome/duotone-regular-svg-icons';
import { Tooltip, TooltipContent } from '@hdir/ui/tooltip';
import { type ClassValue } from 'clsx';
import React, { type JSX, useRef, useState } from 'react';
import { customTwMerge } from '/react4xp/util/customTwMerge';
import { Div, Span } from '/react4xp/components/Element';
import { FontAwesomeIconWrapper as Icon } from '/react4xp/components/FontAwesomeIconWrapper';
import { sanitizeHtml } from '/react4xp/shared/sanitizeHtml';


export function TooltipIcon({
	className,
	html,
}: {
	className?: ClassValue;
	html: string;
}): JSX.Element {
	const [hasFocus, setHasFocus] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const hideTimeoutRef = useRef<number | null>(null);

	function handleEscKey(event: KeyboardEvent): void {
		if (event.code === 'Escape') {
			setIsOpen(false);
		}
	}

	function handleMouseEnter(): void {
		if (hideTimeoutRef.current) {
			clearTimeout(hideTimeoutRef.current);
			hideTimeoutRef.current = null;
		}
		setIsOpen(true);
		document.addEventListener('keydown', handleEscKey);
	}

	function handleMouseLeave(): void {
		hideTimeoutRef.current = setTimeout(() => {
			if (!hasFocus) {
				setIsOpen(false);
			}
			document.removeEventListener('keydown', handleEscKey);
			hideTimeoutRef.current = null;
		}, 300);
	}

	return (
		// NOTE: The Tooltip component handles setting correct aria and role attributes.
		<Div className={customTwMerge('inline-block', className)}>
			<Tooltip isOpen={isOpen}>
				<TooltipContent
					// // @ts-expect-error TODO Frisk This doesn't work!
					// onMouseEnter={() => {
					// 	console.debug('onMouseEnter TooltipContent');
					// 	handleMouseEnter();
					// }}
					// onMouseLeave={() => {
					// 	console.debug('onMouseLeave TooltipContent');
					// 	handleMouseLeave();
					// }}
				>
					<Span
						dangerouslySetInnerHTML={{ __html: sanitizeHtml(html)}}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
					/>
				</TooltipContent>
				<Icon
					// Since this icon can get focus, override the "default" aria-hidden='true' to
					// avoid warning in browser console
					aria-hidden='false'

					aria-label='Vis hjelpetekst'

					className={[
						'color-icon-action-default',
						'[--fa-secondary-opacity:0]',
						'[--fa-secondary-color:--design-color-surface-action-secondary-active]',
						'hover:[--fa-secondary-opacity:0.4]',
					]}

					icon={
						isOpen ? duoRegCircleInfo as IconDefinition :
							duoLightCircleInfo as IconDefinition
					}

					onBlur={() => {
						setHasFocus(false);
						// This breaks WCAG 1.4.13 Hoverable: If pointer hover can trigger the
						// additional content, then the pointer can be moved over the additional
						// content without the additional content disappearing;
						setIsOpen(false);
					}}

					onFocus={() => {
						setHasFocus(true);
						setIsOpen(true);
					}}

					// This only triggers when it already has focus
					onKeyDown={(event) => {
						if (event.code === 'Escape') {
							setIsOpen(false);
						}
					}}

					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					role='button'
					tabIndex={0}
				/>
			</Tooltip>
		</Div>
	);
}
